'use client'

import { cn } from '@/lib/utils'
import { uploadFileToService } from '@/lib/utils/fileUpload'
import Image from 'next/image'
import { useRef, useState } from 'react'
import { FiUpload, FiX } from 'react-icons/fi'
import { toast } from 'react-toastify'

type IssueReportFormProps = {
  isOpen: boolean
  onClose: () => void
  onSubmit: (data: { title: string; description: string; imageUrl?: string }) => Promise<void>
  isSubmitting: boolean
}

export default function IssueReportForm({
  isOpen,
  onClose,
  onSubmit,
  isSubmitting,
}: IssueReportFormProps) {
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [image, setImage] = useState<File | null>(null)
  const [imagePreviewUrl, setImagePreviewUrl] = useState<string | null>(null)
  const [uploadedImageUrl, setUploadedImageUrl] = useState<string | null>(null)
  const [isUploadingImage, setIsUploadingImage] = useState(false)
  const [uploadProgress, setUploadProgress] = useState(0)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const handleImageChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file && file.type.startsWith('image/')) {
      setImage(file)
      const previewUrl = URL.createObjectURL(file)
      setImagePreviewUrl(previewUrl)

      // Upload the image immediately with progress tracking
      setIsUploadingImage(true)
      setUploadProgress(0)
      try {
        const uploadResult = await uploadFileToService(file, (progress) => {
          setUploadProgress(progress)
        })
        if (uploadResult.success && uploadResult.url) {
          setUploadedImageUrl(uploadResult.url)
          setUploadProgress(100)
        } else {
          toast.error(uploadResult.error || 'Failed to upload image')
          // Keep the preview but clear the uploaded URL
          setUploadedImageUrl(null)
        }
      } catch (error) {
        console.error('Image upload error:', error)
        const errorMessage = error instanceof Error ? error.message : 'Failed to upload image'
        toast.error(errorMessage)
        setUploadedImageUrl(null)
      } finally {
        setIsUploadingImage(false)
      }
    }
  }

  const handleRemoveImage = () => {
    if (imagePreviewUrl) {
      URL.revokeObjectURL(imagePreviewUrl)
    }
    setImage(null)
    setImagePreviewUrl(null)
    setUploadedImageUrl(null)
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!title.trim() || !description.trim()) return

    try {
      await onSubmit({
        title: title.trim(),
        description: description.trim(),
        imageUrl: uploadedImageUrl || undefined,
      })

      // Reset form on successful submission
      setTitle('')
      setDescription('')
      handleRemoveImage()
      onClose()
    } catch (error) {
      // Error handling is done by the parent component
      console.error('Issue report submission error:', error)
    }
  }

  const handleCancel = () => {
    setTitle('')
    setDescription('')
    handleRemoveImage()
    onClose()
  }

  if (!isOpen) return null

  return (
    <div className="animate-in slide-in-from-top-2 mb-4 w-full duration-200">
      <div className="w-full rounded-2xl border border-[#E0E0E0] bg-[#F3F3F3] p-4">
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Header */}
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-[#222]">Report Issue</h3>
            <button
              type="button"
              onClick={handleCancel}
              className="flex h-6 w-6 items-center justify-center rounded-full text-[#757575] hover:bg-[#E0E0E0]"
              aria-label="Close issue form"
            >
              <FiX className="h-4 w-4" />
            </button>
          </div>

          {/* Title Input */}
          <div>
            <label htmlFor="issue-title" className="mb-2 block text-sm font-medium text-[#222]">
              Issue Title
            </label>
            <input
              id="issue-title"
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Brief description of the issue"
              className={cn(
                'w-full rounded-lg border border-[#E0E0E0] bg-white px-3 py-2 text-base text-[#222]',
                'placeholder:text-[#757575] focus:border-[#B0B0B0] focus:outline-none'
              )}
              required
              disabled={isSubmitting}
            />
          </div>

          {/* Description Textarea */}
          <div>
            <label
              htmlFor="issue-description"
              className="mb-2 block text-sm font-medium text-[#222]"
            >
              Description
            </label>
            <textarea
              id="issue-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Please describe the issue in detail..."
              rows={4}
              className={cn(
                'w-full resize-none rounded-lg border border-[#E0E0E0] bg-white px-3 py-2 text-base text-[#222]',
                'placeholder:text-[#757575] focus:border-[#B0B0B0] focus:outline-none'
              )}
              required
              disabled={isSubmitting}
            />
          </div>

          {/* Image Upload */}
          <div>
            <label className="mb-2 block text-sm font-medium text-[#222]">
              Attach Image (Optional)
            </label>

            {image && imagePreviewUrl ? (
              <div className="relative flex items-start gap-3 rounded-lg border border-[#E0E0E0] bg-white p-3">
                {/* Upload progress bar */}
                {isUploadingImage && uploadProgress < 100 && (
                  <div className="absolute bottom-0 left-0 right-0 h-1 overflow-hidden rounded-b bg-gray-100">
                    <div
                      className="h-full bg-blue-500 transition-all duration-300"
                      style={{ width: `${uploadProgress}%` }}
                    />
                  </div>
                )}

                <Image
                  src={imagePreviewUrl}
                  alt="Issue attachment preview"
                  className="h-16 w-16 rounded object-cover"
                  width={64}
                  height={64}
                />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium text-[#222]">{image.name}</p>
                  <div className="text-xs text-[#757575]">
                    {(image.size / 1024).toFixed(1)} KB
                    {isUploadingImage && (
                      <span className="ml-2 text-blue-600">Uploading {uploadProgress}%</span>
                    )}
                    {uploadedImageUrl && !isUploadingImage && (
                      <span className="ml-2 text-green-600">✓ Uploaded</span>
                    )}
                    {!uploadedImageUrl && !isUploadingImage && image && (
                      <span className="ml-2 text-red-600">Upload failed</span>
                    )}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={handleRemoveImage}
                  className="text-[#757575] hover:text-red-500"
                  aria-label="Remove image"
                  disabled={isSubmitting}
                >
                  <FiX className="h-4 w-4" />
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className={cn(
                  'flex w-full items-center justify-center gap-2 rounded-lg border border-dashed border-[#E0E0E0]',
                  'bg-white px-3 py-6 text-[#757575] transition-colors hover:border-[#B0B0B0] hover:bg-[#F8F8F8]',
                  { 'cursor-not-allowed opacity-50': isSubmitting }
                )}
                disabled={isSubmitting}
              >
                <FiUpload className="h-4 w-4" />
                <span className="text-sm">Click to upload image</span>
              </button>
            )}

            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleImageChange}
              className="hidden"
              disabled={isSubmitting}
            />
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={handleCancel}
              className="flex-1 rounded-lg border border-[#E0E0E0] bg-white px-4 py-2 text-sm font-medium text-[#222] transition-colors hover:bg-[#F8F8F8]"
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!title.trim() || !description.trim() || isSubmitting}
              className={cn(
                'flex-1 rounded-lg px-4 py-2 text-sm font-medium text-white transition-colors',
                title.trim() && description.trim() && !isSubmitting
                  ? 'bg-[#222] hover:bg-[#333]'
                  : 'cursor-not-allowed bg-[#E0E0E0] text-[#757575]'
              )}
            >
              {isSubmitting ? 'Submitting...' : 'Submit Issue'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
